import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Ban, ChevronDown, ChevronUp, Crown, PauseCircle, PlayCircle, Save, Trash2, X } from 'lucide-react';
import { api } from '../lib/api';
import { DEFAULT_PROFILE_PIC, resolveProfilePic, handleProfilePicError } from '../lib/avatar';

const formatDateTime = (value) => {
  if (!value) return '--';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '--' : parsed.toLocaleString();
};

const formatAmount = (value) => Number(value || 0);

const getInitial = (user = {}) => {
  const label = user.name || user.username || '?';
  return label.replace(/^@/, '').trim().charAt(0).toUpperCase() || '?';
};

const getProfilePicValue = (user = {}) => (
  user.profilePic
  || user.profilePicture
  || user.profileImage
  || user.avatar
  || user.photoURL
  || user.photoUrl
  || user.image
  || user.picture
  || ''
);

const hasUploadedProfilePic = (user = {}) => {
  const profilePic = getProfilePicValue(user);
  return Boolean(profilePic) && resolveProfilePic(profilePic) !== DEFAULT_PROFILE_PIC;
};

const getErrorMessage = (error, fallback = 'Something went wrong') => {
  const detail = error?.response?.data?.detail;

  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((entry) => {
        if (typeof entry === 'string') return entry;
        if (entry && typeof entry === 'object' && typeof entry.msg === 'string') return entry.msg;
        return '';
      })
      .filter(Boolean)
      .join(' • ') || fallback;
  }

  if (detail && typeof detail === 'object') {
    if (typeof detail.message === 'string' && detail.message.trim()) {
      return detail.message;
    }
    try {
      return JSON.stringify(detail);
    } catch {
      return fallback;
    }
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return fallback;
};

const SectionList = ({ title, sectionKey, expandedSections, onToggle, items, loading, renderItem }) => (
  <div className="rounded-[16px] border border-[#ececec] bg-white/80 overflow-hidden">
    <button
      type="button"
      onClick={() => onToggle(sectionKey)}
      className="w-full px-4 py-3 flex items-center justify-between text-left"
    >
      <span className="text-sm font-semibold text-[#000000] font-['Outfit']">{title}</span>
      {expandedSections[sectionKey] ? <ChevronUp className="w-4 h-4 text-[#666666]" /> : <ChevronDown className="w-4 h-4 text-[#666666]" />}
    </button>
    {expandedSections[sectionKey] && (
      <div className="border-t border-[#ececec] px-4 py-3 space-y-2 max-h-[260px] overflow-y-auto bg-white/60">
        {loading ? (
          <p className="text-xs text-[#666666] font-['DM_Sans']">Loading...</p>
        ) : items.length === 0 ? (
          <p className="text-xs text-[#666666] font-['DM_Sans']">No records found.</p>
        ) : (
          items.map(renderItem)
        )}
      </div>
    )}
  </div>
);

const AdminUserProfileModal = ({ userId, isOpen, onClose, onUserUpdated, onUserDeleted }) => {
  const [activeUserId, setActiveUserId] = useState(userId);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyAction, setBusyAction] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [profileDetailsOpen, setProfileDetailsOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    referrals: false,
    referralEarnings: false,
    withdrawals: false,
    poolWins: false,
    topups: false,
  });
  const [sectionData, setSectionData] = useState({
    referrals: { items: [], loading: false },
    referralEarnings: { items: [], loading: false },
    withdrawals: { items: [], loading: false },
    poolWins: { items: [], loading: false },
    topups: { items: [], loading: false },
  });
  const [form, setForm] = useState({
    username: '',
    name: '',
    telegramId: '',
    credits: '0',
    profileScore: '0',
    streak: '0',
    totalPuzzlesSolved: '0',
    rightAnswers: '0',
    wrongAnswers: '0',
    poolWins: '0',
    downlineWins: '0',
  });

  const accuracy = useMemo(() => {
    const total = Number(form.totalPuzzlesSolved || 0);
    const right = Number(form.rightAnswers || 0);
    if (total <= 0) return 0;
    return Math.round((right / total) * 100);
  }, [form.rightAnswers, form.totalPuzzlesSolved]);

  const hydrateForm = (nextUser) => {
    setForm({
      username: nextUser?.username || '',
      name: nextUser?.name || '',
      telegramId: nextUser?.telegramId || '',
      credits: String(nextUser?.credits ?? 0),
      profileScore: String(nextUser?.profileScore ?? 0),
      streak: String(nextUser?.streak ?? 0),
      totalPuzzlesSolved: String(nextUser?.totalPuzzlesSolved ?? 0),
      rightAnswers: String(nextUser?.rightAnswers ?? 0),
      wrongAnswers: String(nextUser?.wrongAnswers ?? 0),
      poolWins: String(nextUser?.poolWins ?? 0),
      downlineWins: String(nextUser?.downlineWins ?? 0),
    });
  };

  useEffect(() => {
    if (isOpen) {
      setActiveUserId(userId);
    }
  }, [isOpen, userId]);

  useEffect(() => {
    if (!isOpen || !activeUserId) return;
    const loadUser = async () => {
      setLoading(true);
      setMessage('');
      setMessageType('info');
      try {
        const { data } = await api.getAdminUserDetail(activeUserId);
        const nextUser = data?.user || null;
        setUser(nextUser);
        hydrateForm(nextUser);
      } catch (error) {
        setMessage(getErrorMessage(error, 'Failed to load user'));
        setMessageType('error');
      } finally {
        setLoading(false);
      }
    };
    loadUser();
  }, [isOpen, activeUserId]);

  useEffect(() => {
    if (!isOpen) {
      setProfileDetailsOpen(false);
      setExpandedSections({ referrals: false, referralEarnings: false, withdrawals: false, poolWins: false, topups: false });
      setSectionData({
        referrals: { items: [], loading: false },
        referralEarnings: { items: [], loading: false },
        withdrawals: { items: [], loading: false },
        poolWins: { items: [], loading: false },
        topups: { items: [], loading: false },
      });
      setMessage('');
      setMessageType('info');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setProfileDetailsOpen(false);
    setExpandedSections({ referrals: false, referralEarnings: false, withdrawals: false, poolWins: false, topups: false });
    setSectionData({
      referrals: { items: [], loading: false },
      referralEarnings: { items: [], loading: false },
      withdrawals: { items: [], loading: false },
      poolWins: { items: [], loading: false },
      topups: { items: [], loading: false },
    });
  }, [isOpen, activeUserId]);

  const sectionLoader = useMemo(() => ({
    referrals: () => api.getAdminUserReferrals(activeUserId),
    referralEarnings: () => api.getAdminUserReferralEarnings(activeUserId),
    withdrawals: () => api.getAdminUserWithdrawals(activeUserId),
    poolWins: () => api.getAdminUserPoolWins(activeUserId),
    topups: () => api.getAdminUserTopups(activeUserId),
  }), [activeUserId]);

  const toggleSection = async (key) => {
    const nextOpen = !expandedSections[key];
    setExpandedSections((prev) => ({ ...prev, [key]: nextOpen }));
    if (!nextOpen || sectionData[key].items.length > 0) return;

    setSectionData((prev) => ({ ...prev, [key]: { ...prev[key], loading: true } }));
    try {
      const { data } = await sectionLoader[key]();
      setSectionData((prev) => ({
        ...prev,
        [key]: { loading: false, items: Array.isArray(data?.items) ? data.items : [] },
      }));
    } catch (error) {
      setMessage(getErrorMessage(error, `Failed to load ${key}`));
      setMessageType('error');
      setSectionData((prev) => ({ ...prev, [key]: { ...prev[key], loading: false } }));
    }
  };

  const handleSave = async () => {
    if (!user?.id) return;
    setSaving(true);
    setMessage('');
    setMessageType('info');
    try {
      const payload = {
        username: form.username,
        name: form.name,
        telegramId: form.telegramId,
        credits: Number(form.credits || 0),
        profileScore: Number(form.profileScore || 0),
        streak: Number(form.streak || 0),
        totalPuzzlesSolved: Number(form.totalPuzzlesSolved || 0),
        rightAnswers: Number(form.rightAnswers || 0),
        wrongAnswers: Number(form.wrongAnswers || 0),
        poolWins: Number(form.poolWins || 0),
        downlineWins: Number(form.downlineWins || 0),
      };
      const { data } = await api.saveAdminUserProfile(user.id, payload);
      const updated = data?.user || null;
      if (updated) {
        setUser(updated);
        hydrateForm(updated);
        if (typeof onUserUpdated === 'function') {
          onUserUpdated(updated);
        }
      }
      setMessage('Saved successfully. Refreshing...');
      setMessageType('success');

      // Preserve current user context so the modal reopens after refresh.
      try {
        sessionStorage.setItem('adminUsersSelectedId', user.id);
      } catch {}

      // Refresh the page so updated values are re-fetched from the database
      // and the admin can visually confirm persistence. The sessionStorage
      // state will restore the same user's profile modal after reload.
      setTimeout(() => {
        try { window.location.reload(); } catch { /* noop */ }
      }, 900);
    } catch (error) {
      setMessage(getErrorMessage(error, 'Failed to save user profile'));
      setMessageType('error');
      setSaving(false);
    }
  };

  const applyQuickAction = async (actionKey, requestFn, successMessage) => {
    setBusyAction(actionKey);
    setMessage('');
    setMessageType('info');
    try {
      const { data } = await requestFn();
      const updated = data?.user;
      if (updated) {
        setUser(updated);
        hydrateForm(updated);
        if (typeof onUserUpdated === 'function') {
          onUserUpdated(updated);
        }
      }
      setMessage(successMessage);
      setMessageType('success');
    } catch (error) {
      setMessage(getErrorMessage(error, 'Action failed'));
      setMessageType('error');
    } finally {
      setBusyAction('');
    }
  };

  const toggleBlock = async () => {
    if (!user) return;
    await applyQuickAction(
      'block',
      () => api.setAdminUserBlock(user.id, !user.isBlocked),
      user.isBlocked ? 'User unblocked.' : 'User blocked.'
    );
  };

  const toggleWithdrawalsPause = async () => {
    if (!user) return;
    await applyQuickAction(
      'withdrawals',
      () => api.setAdminUserWithdrawalsPause(user.id, !user.withdrawalsPaused),
      user.withdrawalsPaused ? 'Withdrawals resumed.' : 'Withdrawals paused.'
    );
  };

  const handleSwan = async () => {
    if (!user) return;
    await applyQuickAction('swan', () => api.setAdminUserSwan(user.id), 'Swan scheduled for upcoming draw.');
  };

  const handleDeleteUser = async () => {
    if (!user?.id) return;
    const displayLabel = user.username || user.name || user.id;
    const confirmed = window.confirm(
      `Are you sure you want to PERMANENTLY delete ${displayLabel}?\n\n` +
      `This will remove the user and ALL linked records (credits, profile score, stats, profile picture, referrals, activity logs, withdrawal history, top-up history) from the database.\n\n` +
      `This action cannot be undone.`
    );
    if (!confirmed) return;

    setBusyAction('delete');
    setMessage('');
    setMessageType('info');
    try {
      await api.deleteAdminUser(user.id);
      setMessage('User deleted.');
      setMessageType('success');

      // Clear any preserved selection so the modal does not try to reopen
      // this user after a refresh.
      try {
        sessionStorage.removeItem('adminUsersSelectedId');
      } catch {}

      if (typeof onUserDeleted === 'function') {
        onUserDeleted(user.id);
      }

      // Close the popup after a brief moment so the success message is visible.
      setTimeout(() => {
        try {
          if (typeof onClose === 'function') onClose();
        } catch {}
      }, 600);
    } catch (error) {
      setMessage(getErrorMessage(error, 'Failed to delete user'));
      setMessageType('error');
    } finally {
      setBusyAction('');
    }
  };

  const openReferralUser = (referralUserId) => {
    if (!referralUserId || referralUserId === activeUserId) return;
    setMessage('');
    setMessageType('info');
    setActiveUserId(referralUserId);
    try {
      sessionStorage.setItem('adminUsersSelectedId', referralUserId);
    } catch {}
  };

  const renderCardInput = (label, key, type = 'number') => (
    <label className="block">
      <span className="mb-1 block text-xs text-[#666666] font-['DM_Sans']">{label}</span>
      <input
        type={type}
        value={form[key]}
        onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
        className="w-full rounded-[12px] border border-[#ececec] bg-white px-3 py-2 text-sm"
      />
    </label>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) onClose();
    }}>
      <DialogContent
        hideCloseButton
        className="max-w-3xl glass-panel border-white/40 max-h-[92vh] overflow-y-auto"
        style={{ backgroundColor: '#f2619c' }}
      >
        <DialogHeader className="flex-row items-center justify-end space-y-0">
          <DialogTitle className="sr-only">User Profile</DialogTitle>
          <button type="button" onClick={onClose} className="h-9 w-9 rounded-full bg-white/70 flex items-center justify-center">
            <X className="w-4 h-4 text-[#666666]" />
          </button>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-[#666666] font-['DM_Sans']">Loading user...</p>
        ) : user ? (
          <div className="space-y-5">
            <div className="flex items-center gap-4">
              <img src={resolveProfilePic(user.profilePic)} alt={user.name} onError={handleProfilePicError} className="h-24 w-24 rounded-full object-cover border border-white/80" />
              <div>
                <h3 className="text-2xl font-bold text-[#000000] font-['Outfit']">{user.name}</h3>
                <p className="text-sm text-[#666666] font-['DM_Sans']">{user.email || 'No email'}</p>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  <p className="text-sm text-[#666666] font-['DM_Sans']">Joined {formatDateTime(user.createdAt)}</p>
                  <span
                    className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-1 rounded-full flex-shrink-0 ${
                      user.isActiveForPeriod
                        ? 'bg-green-100 text-green-700'
                        : 'bg-[#f4f4f4] text-[#777777]'
                    }`}
                  >
                    {user.isActiveForPeriod ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-[16px] border border-[#ececec] bg-white/80 overflow-hidden">
              <button
                type="button"
                onClick={() => setProfileDetailsOpen((prev) => !prev)}
                className="w-full px-4 py-3 flex items-center justify-between text-left"
              >
                <span className="text-sm font-semibold text-[#000000] font-['Outfit']">Profile Details</span>
                {profileDetailsOpen ? <ChevronUp className="w-4 h-4 text-[#666666]" /> : <ChevronDown className="w-4 h-4 text-[#666666]" />}
              </button>
              {profileDetailsOpen && (
                <div className="border-t border-[#ececec] px-4 py-3 bg-white/60">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {renderCardInput('Display Name', 'name', 'text')}
                    {renderCardInput('@Username', 'username', 'text')}
                    {renderCardInput('Telegram ID', 'telegramId', 'text')}
                    {renderCardInput('Credits', 'credits')}
                    {renderCardInput('Profile Score', 'profileScore')}
                    {renderCardInput('Streak', 'streak')}
                    {renderCardInput('Total Puzzles Solved', 'totalPuzzlesSolved')}
                    {renderCardInput('Right Answers', 'rightAnswers')}
                    {renderCardInput('Wrong Answers', 'wrongAnswers')}
                    {renderCardInput('Pool Wins', 'poolWins')}
                    {renderCardInput('Downline Wins', 'downlineWins')}
                    <div className="rounded-[12px] border border-[#ececec] bg-white px-3 py-2 flex flex-col justify-center">
                      <span className="text-xs text-[#666666] font-['DM_Sans']">Accuracy</span>
                      <span className="text-lg font-bold text-[#000000] font-['Outfit']">{accuracy}%</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-4 gap-2">
              <button
                type="button"
                onClick={handleSwan}
                disabled={busyAction === 'swan' || user.swanDone}
                className={`aspect-square rounded-2xl flex flex-col items-center justify-center gap-1 px-1 text-center disabled:opacity-60 disabled:cursor-not-allowed ${user.swanDone ? 'bg-green-100 text-green-700' : 'bg-[#FFC40C] text-[#4d3800]'}`}
              >
                <Crown className="w-5 h-5" />
                <span className="text-[10px] font-semibold leading-tight font-['DM_Sans']">{user.swanDone ? 'Done' : 'Swan'}</span>
              </button>
              <button
                type="button"
                onClick={toggleBlock}
                disabled={busyAction === 'block'}
                className={`aspect-square rounded-2xl flex flex-col items-center justify-center gap-1 px-1 text-center disabled:opacity-60 disabled:cursor-not-allowed ${user.isBlocked ? 'bg-green-100 text-green-700' : 'bg-[#FFC40C] text-[#4d3800]'}`}
              >
                <Ban className="w-5 h-5" />
                <span className="text-[10px] font-semibold leading-tight font-['DM_Sans']">{user.isBlocked ? 'Unblock' : 'Block User'}</span>
              </button>
              <button
                type="button"
                onClick={toggleWithdrawalsPause}
                disabled={busyAction === 'withdrawals'}
                className={`aspect-square rounded-2xl flex flex-col items-center justify-center gap-1 px-1 text-center disabled:opacity-60 disabled:cursor-not-allowed ${user.withdrawalsPaused ? 'bg-green-100 text-green-700' : 'bg-[#FFC40C] text-[#4d3800]'}`}
              >
                {user.withdrawalsPaused ? <PlayCircle className="w-5 h-5" /> : <PauseCircle className="w-5 h-5" />}
                <span className="text-[10px] font-semibold leading-tight font-['DM_Sans']">{user.withdrawalsPaused ? 'Resume' : 'Pause'}</span>
              </button>
              <button
                type="button"
                onClick={handleDeleteUser}
                disabled={busyAction === 'delete'}
                data-testid="admin-dufd-btn"
                title="Delete User From Database (permanent)"
                className="aspect-square rounded-2xl flex flex-col items-center justify-center gap-1 px-1 text-center bg-red-600 text-white border border-red-700 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-5 h-5" />
                <span className="text-[10px] font-semibold leading-tight font-['DM_Sans']">{busyAction === 'delete' ? '...' : 'DUFD'}</span>
              </button>
            </div>

            {message && (
              <p className={`text-sm rounded-[12px] p-3 font-['DM_Sans'] ${messageType === 'success' ? 'text-green-700 bg-green-50 border border-green-200' : messageType === 'error' ? 'text-red-700 bg-red-50 border border-red-200' : 'text-[#333333] bg-white/70'}`}>
                {message}
              </p>
            )}

            <SectionList
              title="User's Referral List"
              sectionKey="referrals"
              expandedSections={expandedSections}
              onToggle={toggleSection}
              items={sectionData.referrals.items}
              loading={sectionData.referrals.loading}
              renderItem={(item) => {
                const displayName = item.name || item.username || 'Unknown user';
                const showProfilePic = hasUploadedProfilePic(item);

                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => openReferralUser(item.id)}
                    className="w-full rounded-[12px] border border-[#ececec] bg-white px-3 py-2 flex items-center gap-3 text-left hover:border-[#9333EA]/40 hover:bg-[#faf5ff] active:scale-[0.99] transition-all"
                  >
                    <div className="shrink-0 h-11 w-11 rounded-full overflow-hidden bg-[#9333EA]/10 flex items-center justify-center border border-[#ececec]">
                      {showProfilePic ? (
                        <img
                          src={resolveProfilePic(getProfilePicValue(item))}
                          alt={displayName}
                          onError={handleProfilePicError}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="text-sm font-bold text-[#9333EA] font-['Outfit']">{getInitial(item)}</span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-[#000000] font-['Outfit'] truncate">{displayName}</p>
                      <p className="text-xs text-[#666666] font-['DM_Sans'] truncate">{item.username || 'No username'} • Credits {formatAmount(item.credits)}</p>
                    </div>
                  </button>
                );
              }}
            />

            <SectionList
              title="Referral Earnings History"
              sectionKey="referralEarnings"
              expandedSections={expandedSections}
              onToggle={toggleSection}
              items={sectionData.referralEarnings.items}
              loading={sectionData.referralEarnings.loading}
              renderItem={(item) => (
                <div key={item.id || `${item.referredUserId}-${item.createdAt}`} className="rounded-[12px] border border-[#ececec] bg-white px-3 py-2">
                  <p className="text-sm font-semibold text-[#000000] font-['Outfit']">+{formatAmount(item.amount)} credits</p>
                  <p className="text-xs text-[#666666] font-['DM_Sans']">{formatDateTime(item.createdAt)}</p>
                </div>
              )}
            />

            <SectionList
              title="Withdrawal History"
              sectionKey="withdrawals"
              expandedSections={expandedSections}
              onToggle={toggleSection}
              items={sectionData.withdrawals.items}
              loading={sectionData.withdrawals.loading}
              renderItem={(item) => (
                <div key={item.id} className="rounded-[12px] border border-[#ececec] bg-white px-3 py-2">
                  <p className="text-sm font-semibold text-[#000000] font-['Outfit']">{formatAmount(item.requestedAmount || item.finalAmount)} credits</p>
                  <p className="text-xs text-[#666666] font-['DM_Sans']">{item.status} • {formatDateTime(item.createdAt)}</p>
                </div>
              )}
            />

            <SectionList
              title="Winning History"
              sectionKey="poolWins"
              expandedSections={expandedSections}
              onToggle={toggleSection}
              items={sectionData.poolWins.items}
              loading={sectionData.poolWins.loading}
              renderItem={(item) => (
                <div key={item.id} className="rounded-[12px] border border-[#ececec] bg-white px-3 py-2">
                  <p className="text-sm font-semibold text-[#000000] font-['Outfit']">Pool win • Won {formatAmount(item.profileScoreWon ?? item.amountWon ?? item.rewardAmount ?? item.resultAmount ?? item.profileScore)}</p>
                  <p className="text-xs text-[#666666] font-['DM_Sans']">{formatDateTime(item.winnerAt || item.createdAt)}</p>
                </div>
              )}
            />

            <SectionList
              title="Top-up History"
              sectionKey="topups"
              expandedSections={expandedSections}
              onToggle={toggleSection}
              items={sectionData.topups.items}
              loading={sectionData.topups.loading}
              renderItem={(item) => (
                <div key={item.id} className="rounded-[12px] border border-[#ececec] bg-white px-3 py-2">
                  <p className="text-sm font-semibold text-[#000000] font-['Outfit']">Top-up {formatAmount(item.amount)} credits</p>
                  <p className="text-xs text-[#666666] font-['DM_Sans']">{formatDateTime(item.createdAt)}</p>
                </div>
              )}
            />

            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="w-full rounded-full bg-gradient-to-r from-[#9333EA] to-[#EC4899] py-3 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        ) : (
          <p className="text-sm text-red-600 font-['DM_Sans']">{message || 'User not found.'}</p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdminUserProfileModal;
